import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Translate, CaretDown, Check } from "@phosphor-icons/react";
import { useI18n } from "@/contexts/I18nContext";

/**
 * Compact language selector. Mounts in the public Landing nav AND inside the
 * authenticated app top bar. Persistence + RTL flip + backend `/profile` sync
 * are all handled inside `I18nContext.changeLang`.
 *
 * `compact` — hides the language label on tight viewports.
 */
export default function LanguageSwitcher({ compact = false, alignRight = true }) {
  const { lang, changeLang, currency, country, SUPPORTED_LANGS } = useI18n();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const current = SUPPORTED_LANGS.find((l) => l.code === lang) || SUPPORTED_LANGS[0];

  const pick = async (code) => {
    setOpen(false);
    if (code !== lang) await changeLang(code);
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        type="button"
        data-testid="language-switcher-btn"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex items-center gap-2 rounded-full border border-[var(--border-strong)] bg-[var(--surface-strong)] backdrop-blur px-3 h-9 text-xs font-medium hover:bg-[var(--surface-2)] transition"
      >
        <Translate size={14} weight="duotone" className="text-[var(--accent)]" />
        <span className={compact ? "hidden md:inline" : ""}>{current.native}</span>
        <CaretDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15 }}
            className={`absolute top-11 z-30 w-60 rounded-xl border border-[var(--border-strong)] bg-[var(--bg-2)] backdrop-blur-xl shadow-xl py-2 ${alignRight ? "right-0" : "left-0"}`}
            role="listbox"
            data-testid="language-switcher-menu"
          >
            <div className="px-3 pb-2 mb-1 border-b border-[var(--border)] flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-widest text-[var(--text-3)]">Language</span>
              <span className="text-[10px] font-mono text-[var(--text-3)]" data-testid="language-switcher-currency-hint">
                {country ? `${country} · ` : ""}{currency}
              </span>
            </div>
            <div className="max-h-72 overflow-y-auto">
              {SUPPORTED_LANGS.map((l) => {
                const active = l.code === lang;
                return (
                  <button
                    key={l.code}
                    type="button"
                    role="option"
                    aria-selected={active}
                    onClick={() => pick(l.code)}
                    data-testid={`language-switcher-option-${l.code}`}
                    className={`w-full flex items-center gap-3 text-start px-3 py-2 text-sm transition hover:bg-[var(--surface)] ${
                      active ? "bg-[var(--surface)]" : ""
                    }`}
                  >
                    <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--text-3)] w-7 shrink-0">{l.code}</span>
                    <span className="flex-1 min-w-0">
                      <span className="block leading-tight font-medium" lang={l.code}>{l.native}</span>
                      <span className="block text-[10px] text-[var(--text-3)] mt-0.5">{l.name} · {l.currency}</span>
                    </span>
                    {active && <Check size={14} weight="bold" className="text-[var(--accent)]" />}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
